/**
 * 🚀 Application Entry Point
 */

import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';

import { router } from './router';
import { authService } from '@application/services/AuthService';
import { unlockAudio } from '@infrastructure/audio/notificationSound';
import './index.css';

// ═══════════════════════════════════════════════════
// React Query Config
// ═══════════════════════════════════════════════════
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 دقائق
      gcTime: 10 * 60 * 1000, // 10 دقائق
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

// ═══════════════════════════════════════════════════
// Root App
// ═══════════════════════════════════════════════════
function App() {
  useEffect(() => {
    // Subscribe to Firebase auth state
    const unsubscribe = authService.onAuthStateChange();
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // 🔓 يفتح تشغيل صوت الإشعارات على أول تفاعل (autoplay policy).
    //    once: true → يُزال المستمع تلقائياً بعد أول حدث.
    window.addEventListener('pointerdown', unlockAudio, { once: true });
    window.addEventListener('keydown', unlockAudio, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <Toaster
        position="top-center"
        dir="rtl"
        richColors
        closeButton
        duration={4000}
        gap={10}
        toastOptions={{
          style: {
            fontFamily: 'Tajawal, Cairo, sans-serif',
            fontSize: '0.875rem',
            fontWeight: 600,
            borderRadius: '1rem',
            padding: '14px 16px',
            boxShadow: '0 8px 30px rgba(88, 28, 135, 0.15)',
          },
          classNames: {
            title: 'text-sm font-bold leading-snug',
            description: 'text-xs leading-relaxed opacity-90 mt-0.5',
            actionButton: 'font-bold',
          },
        }}
      />
    </QueryClientProvider>
  );
}

// ⚠️ StrictMode + Firebase 11.x = double-mount يكسر onSnapshot listeners
//    (INTERNAL ASSERTION FAILED — WatchChangeAggregator)
//    نخليه في prod فقط (في dev ما يأثر شيء عملي — الـ React 19 يكشف الـ bugs بطرق ثانية)
const root = createRoot(document.getElementById('root')!);

if (import.meta.env.PROD) {
  root.render(
    <StrictMode>
      <App />
    </StrictMode>
  );
} else {
  root.render(<App />);
}
